import React, { useEffect, useState } from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useGetWorkspaceGoggles } from 'hooks/goggles/useGetWorkspaceGoggles';
import { useUpdateWorkspaceGoggle } from 'hooks/goggles/useUpdateWorkspaceGoggle';
import { useDeleteWorkspaceGoggle } from 'hooks/goggles/useDeleteWorkspaceGoggle';
import { IVrGoggleModel, VrGoggleStatus } from 'api/models/response/goggles/IVrGoggleModel';
import { formatDateToCustomString } from 'helpers/dateHelper';
import { handleApiError } from 'helpers/handleApiError';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { SaveButton } from '../profile/DisplayNameSection/DisplayNameSection.styles';

export const GogglesTab = () => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { goggles, gogglesLoading, gogglesRefetching } = useGetWorkspaceGoggles();
    const { updateGoggle, updatingGoggle } = useUpdateWorkspaceGoggle();
    const { deleteGoggle, deletingGoggle } = useDeleteWorkspaceGoggle();

    const [draftNames, setDraftNames] = useState<Record<string, string>>({});
    const [goggleToDelete, setGoggleToDelete] = useState<IVrGoggleModel | null>(null);
    const [savingGoggleId, setSavingGoggleId] = useState<string | null>(null);
    const [deletingGoggleId, setDeletingGoggleId] = useState<string | null>(null);

    useEffect(() => {
        setDraftNames((prev) => {
            const next: Record<string, string> = {};
            for (const goggle of goggles) {
                next[goggle.id] = prev[goggle.id] !== undefined ? prev[goggle.id] : goggle.displayName;
            }
            return next;
        });
    }, [goggles]);

    const handleSave = async (goggle: IVrGoggleModel) => {
        const displayName = draftNames[goggle.id]?.trim();
        if (!displayName || displayName === goggle.displayName) {
            return;
        }

        setSavingGoggleId(goggle.id);
        try {
            await updateGoggle({ goggleId: goggle.id, displayName });
            enqueueSnackbar(t('gogglesManagement.updateSuccess'), { variant: 'success' });
        } catch (error) {
            handleApiError(error, { t, enqueueSnackbar });
        } finally {
            setSavingGoggleId(null);
        }
    };

    const handleDelete = async () => {
        if (!goggleToDelete) {
            return;
        }

        setDeletingGoggleId(goggleToDelete.id);
        try {
            await deleteGoggle(goggleToDelete.id);
            enqueueSnackbar(t('gogglesManagement.deleteSuccess'), { variant: 'success' });
        } catch (error) {
            handleApiError(error, { t, enqueueSnackbar });
        } finally {
            setDeletingGoggleId(null);
            setGoggleToDelete(null);
        }
    };

    if (gogglesLoading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Typography variant="h6">{t('gogglesManagement.title')}</Typography>
                {gogglesRefetching && <CircularProgress size={16} />}
            </Box>

            {goggles.length === 0 ? (
                <Typography color="textSecondary">{t('gogglesManagement.empty')}</Typography>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mx: 0.5, width: 'auto' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('gogglesManagement.name')}</TableCell>
                                <TableCell>{t('gogglesManagement.deviceId')}</TableCell>
                                <TableCell>{t('gogglesManagement.status')}</TableCell>
                                <TableCell>{t('gogglesManagement.registeredAt')}</TableCell>
                                <TableCell align="right">{t('gogglesManagement.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {goggles.map((goggle) => {
                                const draftName = draftNames[goggle.id] ?? goggle.displayName;
                                const isDirty = draftName.trim() !== goggle.displayName;
                                const isSaving = savingGoggleId === goggle.id && updatingGoggle;
                                const isDeleting = deletingGoggleId === goggle.id && deletingGoggle;

                                return (
                                    <TableRow key={goggle.id}>
                                        <TableCell>
                                            <TextField
                                                value={draftName}
                                                size="small"
                                                fullWidth
                                                disabled={isSaving || isDeleting}
                                                onChange={(e) =>
                                                    setDraftNames((prev) => ({ ...prev, [goggle.id]: e.target.value }))
                                                }
                                                onBlur={() => {
                                                    if (isSaving || isDeleting) {
                                                        return;
                                                    }
                                                    setDraftNames((prev) => ({ ...prev, [goggle.id]: goggle.displayName }));
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>{goggle.externalDeviceId}</TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={1}>
                                                <Chip
                                                    size="small"
                                                    label={goggle.isConnected ? t('gogglesManagement.connected') : t('gogglesManagement.disconnected')}
                                                    color={goggle.isConnected ? 'success' : 'default'}
                                                />
                                                {goggle.status === VrGoggleStatus.Disabled && (
                                                    <Chip size="small" label={t('gogglesManagement.disabled')} color="warning" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{formatDateToCustomString(goggle.registeredAt, true)}</TableCell>
                                        <TableCell align="right">
                                            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                                                {isDirty && (
                                                    <SaveButton
                                                        variant="outlined"
                                                        color="primary"
                                                        loading={isSaving}
                                                        disabled={isDeleting}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() => handleSave(goggle)}
                                                    >
                                                        {t('general.save')}
                                                    </SaveButton>
                                                )}
                                                <IconButton
                                                    onClick={() => setGoggleToDelete(goggle)}
                                                    disabled={isSaving || isDeleting}
                                                >
                                                    {isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ConfirmationDialog
                open={!!goggleToDelete}
                message={t('gogglesManagement.deleteConfirm', { name: goggleToDelete?.displayName ?? '' })}
                handleClose={() => setGoggleToDelete(null)}
                handleConfirm={handleDelete}
            />
        </Box>
    );
};
